import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiAlertCircle, FiCheck, FiChevronDown, FiRefreshCw } from "react-icons/fi";
import { useDismiss } from "../../../hooks/useDismiss";
import { Action } from "../shared";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Saved, TEAM_CT, TEAM_T, splitName } from "./data";

const Wrap = styled.div`
  position: relative;
  min-width: 0;
  display: grid;
  > button.trigger {
    width: 100%;
  }
`;
const Pop = styled.div<{ $up: boolean }>`
  position: absolute;
  left: 0;
  ${(p) => (p.$up ? "bottom: calc(100% + 8px);" : "top: calc(100% + 8px);")}
  z-index: 30;
  width: min(340px, calc(100vw - 24px));
  display: grid;
  gap: 4px;
  padding: 8px;
  border-radius: 14px;
  background: ${(p) => p.theme.colors.surfaceHover};
  border: 1px solid ${(p) => p.theme.colors.borderLight};
  box-shadow: 0 20px 48px #000a;
  animation: pop-in ${(p) => p.theme.motion.fast} ${(p) => p.theme.motion.ease};
  @keyframes pop-in {
    from {
      opacity: 0;
      transform: translateY(${(p) => (p.$up ? "4px" : "-4px")});
    }
  }
  header {
    padding: 6px 8px 8px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 560px) {
    position: fixed;
    left: 12px;
    right: 12px;
    bottom: 12px;
    top: auto;
    width: auto;
    box-shadow: 0 0 0 100vmax #05070acc;
  }
`;
const Row = styled.div<{ $on: boolean; $off?: boolean }>`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  background: ${(p) => (p.$on ? p.theme.colors.primarySoft : p.theme.colors.input)};
  box-shadow: inset 0 0 0 1px ${(p) => (p.$on ? "#ff9b5733" : p.theme.colors.border)};
  opacity: ${(p) => (p.$off ? 0.55 : 1)};
  .top {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .badge {
    flex: none;
    display: grid;
    place-items: center;
    min-width: 34px;
    height: 26px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 800;
    background: ${(p) => p.theme.colors.navigation};
    color: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.textSecondary)};
    box-shadow: inset 0 0 0 1px ${(p) => (p.$on ? "#ff9b5755" : p.theme.colors.border)};
  }
  .name {
    font-size: 14px;
    font-weight: 650;
  }
  .state {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 650;
    color: ${(p) => (p.$on ? p.theme.colors.primary : p.theme.colors.textMuted)};
    svg {
      width: 14px;
      height: 14px;
    }
  }
  .diff {
    display: grid;
    gap: 2px;
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textSecondary};
    strong {
      color: ${(p) => p.theme.colors.text};
      font-weight: 650;
      overflow-wrap: anywhere;
    }
  }
  button.go {
    min-height: 40px;
    padding-block: 8px;
    font-size: 14px;
  }
`;
const Err = styled.div`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 18px;
  background: ${(p) => p.theme.colors.dangerSoft};
  color: ${(p) => p.theme.colors.danger};
  box-shadow: inset 0 0 0 1px #ff8a9133;
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    font-weight: 650;
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.input};
    &:hover {
      background: ${(p) => p.theme.colors.border};
    }
  }
`;

interface Props {
  saved: Saved;
  inventory: Saved[];
  busy?: boolean;
  /** Переключает сторону и бросает ошибку, если сохранить не удалось. */
  onToggle: (team: number) => Promise<void>;
}

const sideShort = (team: number) => (team === TEAM_T ? "T" : "CT");

/** Меню «Экипировка»: для каждой стороны видно, что стоит сейчас и что изменится. */
export default function EquipMenu({ saved, inventory, busy, onToggle }: Props) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [pending, setPending] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setFailed(null);
  }, []);
  useDismiss(open, close, box, trigger);

  const equipped = saved.teams.length > 0;
  const { skin, model } = splitName(saved.item.name);
  const shortName = skin || model;

  // Открываем вниз, если места хватает, иначе вверх; пересчитываем при прокрутке и ресайзе.
  useLayoutEffect(() => {
    if (!open || !pop.current || !trigger.current) return;
    const place = () => {
      if (!pop.current || !trigger.current) return;
      const h = pop.current.offsetHeight + 16;
      const r = trigger.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      setUp((cur) => (cur ? !(r.top < h && below > h) : below < h && r.top > h));
    };
    place();
    // Если ни вверх, ни вниз меню не помещается целиком, подкручиваем страницу к нему.
    requestAnimationFrame(() => pop.current?.scrollIntoView({ block: "nearest" }));
    pop.current.querySelector<HTMLElement>("button.go:not(:disabled)")?.focus({ preventScroll: true });
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  async function run(team: number) {
    setPending(team);
    setFailed(null);
    try {
      await onToggle(team);
      setOpen(false);
    } catch {
      setFailed(team);
    } finally {
      setPending(null);
    }
  }

  const sideName = (team: number) => t(team === TEAM_T ? "skins.side_t_name" : "skins.side_ct_name");

  return (
    <Wrap ref={box}>
      <Action
        ref={trigger}
        type="button"
        className="trigger"
        $variant={equipped ? undefined : "primary"}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${t(equipped ? "skins.equip_menu" : "skins.equip")}: ${saved.item.name}`}
        onClick={() => setOpen((v) => !v)}
      >
        {t(equipped ? "skins.equip_menu" : "skins.equip")}
        <FiChevronDown aria-hidden="true" style={{ marginLeft: -2, opacity: 0.8 }} />
      </Action>
      {open && (
        <Pop
          ref={pop}
          $up={up}
          role="dialog"
          aria-label={t("skins.equip_menu_title", { name: saved.item.name })}
        >
          <header>{t("skins.equip_menu_title", { name: shortName })}</header>
          {[TEAM_T, TEAM_CT].map((team) => {
            const allowed = !!saved.item.allowedTeams?.includes(team);
            const on = saved.teams.includes(team);
            const other = on
              ? undefined
              : inventory.find(
                  (o) =>
                    o.id !== saved.id &&
                    o.teams.includes(team) &&
                    o.item.kind === saved.item.kind &&
                    (o.item.kind !== "weapon" || o.item.weapon === saved.item.weapon),
                );
            const side = sideShort(team);
            const working = pending === team;
            return (
              <Row key={team} $on={on} $off={!allowed}>
                <div className="top">
                  <span className="badge">{side}</span>
                  <span className="name">{sideName(team).split(" · ")[0]}</span>
                  <span className="state">
                    {on ? (
                      <>
                        <FiCheck aria-hidden="true" />
                        {t("skins.equip_this", { side })}
                      </>
                    ) : allowed ? (
                      t("skins.status_none")
                    ) : (
                      t("skins.equip_incompatible", { side })
                    )}
                  </span>
                </div>
                {allowed && !on && (
                  <div className="diff">
                    <span>
                      {other
                        ? t("skins.equip_now", { side, name: "" })
                        : t("skins.equip_none_now", { side })}
                      {other && <strong>{other.item.name}</strong>}
                    </span>
                    {other && (
                      <span>
                        {t("skins.equip_action", { name: "" })}
                        <strong>{shortName}</strong>
                      </span>
                    )}
                  </div>
                )}
                {allowed && (
                  <Action
                    type="button"
                    className="go"
                    $variant={on ? undefined : "primary"}
                    disabled={busy || pending !== null}
                    aria-busy={working}
                    onClick={() => run(team)}
                  >
                    {working
                      ? t("skins.equip_saving")
                      : t(on ? "skins.unequip_for" : "skins.equip_for", { side })}
                  </Action>
                )}
              </Row>
            );
          })}
          {failed !== null && (
            <Err role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{t("skins.equip_error")}</span>
              <button type="button" onClick={() => run(failed)}>
                <FiRefreshCw aria-hidden="true" />
                {t("common.retry")}
              </button>
            </Err>
          )}
        </Pop>
      )}
    </Wrap>
  );
}
